import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { AppDispatch } from "state/store"
import { Customer } from "types"
import useCustomers from "hooks/useCustomers"
import { decodeFirebaseError } from "utils/firebaseHelpers"
import { logger } from "utils/secureLogger"
import Button from "components/inputs/button"
import ButtonGroup from "components/buttons/buttonGroup"
import Section from "components/layout/section"
import ProcessingButton from "components/inputs/processingButton"
import Form from "components/forms/form"
import FormErrors from "components/formParts/formErrors"
import TextInput from "components/inputs/textInput"
import { ReactComponent as CloseIcon } from "assets/icons/close.svg"
import { ReactComponent as DeleteIcon } from "assets/icons/delete.svg"

interface DeleteCustomerFormProps {
    customer: Customer
    storageId: string
    customerId: string
    onCancel: () => void
    onSuccess?: () => void
}

const DeleteCustomerForm: React.FC<DeleteCustomerFormProps> = ({
    customer,
    storageId,
    customerId,
    onCancel,
    onSuccess
}) => {
    const { t } = useTranslation()
    const dispatch = useDispatch<AppDispatch>()
    const { deleteCustomerAsync, isDeleting } = useCustomers({ storageId })
    
    const [confirmation, setConfirmation] = useState('')
    const [confirmed, setConfirmed] = useState(false)
    const [errors, setErrors] = useState<string[]>([])

    // Handle confirmation change.
    const handleConfirmationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setConfirmation(e.target.value)
        setConfirmed(e.target.value === customer.name)
    }

    const handleSubmit = async (event: React.FormEvent) => {
        logger.debug('Delete customer form submitted', undefined, 'DeleteCustomer')
        setErrors([])
        event.preventDefault()
        
        if (!confirmed) return
        
        try {
            await deleteCustomerAsync({ storageId, customerId })
            logger.info('Customer deleted successfully', { storageId, customerId, customerName: customer.name })
            if (onSuccess) onSuccess()
        } catch (e: unknown) {
            const errorMessage = decodeFirebaseError(e as string)
            setErrors([errorMessage])
            logger.error('Customer deletion failed', { error: e, storageId, customerId })
        }
    }

    return (
        <Form onSubmit={handleSubmit} isLoading={isDeleting}>
            <Section>
                <p>{t('confirmDeleteCustomer', { customerName: customer.name })}</p>
                <TextInput 
                    label={t('typeCustomerNameToConfirm')} 
                    value={confirmation} 
                    onChange={handleConfirmationChange} 
                    placeholder={customer.name} 
                />
                
                {errors.length > 0 && <FormErrors errors={errors} />}
                
                <ButtonGroup>
                    <Button wideOnMobile
                        design="optional"
                        onClick={onCancel}
                        disabled={isDeleting}
                        type="button"
                    >
                        <CloseIcon />
                        <span>{t('buttonCancel')}</span>
                    </Button>
                    
                    {isDeleting ? (
                        <ProcessingButton design="danger" />
                    ) : (
                        <Button wideOnMobile type="submit" design="danger" disabled={!confirmed}>
                            <DeleteIcon />
                            <span>{t('buttonDelete')}</span>
                        </Button>
                    )}
                </ButtonGroup>
            </Section>
        </Form>
    )
}

export default DeleteCustomerForm
